import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Text,
  VStack,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { Link as RouterLink, useParams } from "react-router-dom";
import PaginatedListToolbar from "../../../common/PaginatedListToolbar";
import PaginationControls from "../../../common/PaginationControls";
import usePaginatedList from "../../../common/usePaginatedList";
import { getNetworkReportOrganizations } from "../networkReportApi";
import OrganizationReportCardList from "../components/OrganizationReportCardList";
import OrganizationReportTable from "../components/OrganizationReportTable";

const searchMatcher = (organization, normalizedSearch) =>
  [
    organization.organizationName,
    organization.surveyName,
    organization.status,
    ...organization.strengths.map((factor) => factor.name),
    ...organization.developmentAreas.map((factor) => factor.name),
  ].some((value) => value?.toString().toLowerCase().includes(normalizedSearch));

const NetworkReportOrganizationsTab = () => {
  const { networkId } = useParams();
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setOrganizations(await getNetworkReportOrganizations(networkId));
    } catch (err) {
      console.error("Error loading network report organizations:", err);
      setError("Could not load network report schools.");
      toast({ title: "Could not load network report schools", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [networkId, toast]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const sortOptions = useMemo(() => [
    { key: "organization", label: "School", getSortValue: (organization) => organization.organizationName },
    { key: "completion", label: "Completion", getSortValue: (organization) => organization.completionRate ?? -1 },
    { key: "stability", label: "Overall Stability", getSortValue: (organization) => organization.overallStabilityIndex ?? -1 },
  ], []);

  const {
    paginatedItems,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSortDir,
    setSortKey,
  } = usePaginatedList({
    items: organizations,
    sortOptions,
    initialSortKey: "organization",
    initialSortDir: "asc",
    initialPageSize: 25,
    searchMatcher,
  });

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" label="Loading network report schools" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align={{ base: "start", md: "center" }} flexWrap="wrap">
        <Box>
          <Heading size="md">Schools</Heading>
          <Text mt={1} color={mutedColor}>
            Review school-level report summaries for this network.
          </Text>
        </Box>
        <Button
          as={RouterLink}
          to={`/app/network/${networkId}/organizations`}
          size="sm"
          variant="outline"
        >
          School directory
        </Button>
      </HStack>

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
        <CardBody>
          <PaginatedListToolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            searchPlaceholder="Search schools"
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            sortKey={sortKey}
            sortDir={sortDir}
            sortOptions={sortOptions}
            onSortKeyChange={setSortKey}
            onSortDirChange={setSortDir}
          />
        </CardBody>
      </Card>

      {totalCount === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No schools</Heading>
            <Text color={mutedColor}>No school report data is available for this network yet.</Text>
          </CardBody>
        </Card>
      ) : isMobile ? (
        <OrganizationReportCardList organizations={paginatedItems} networkId={networkId} />
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <OrganizationReportTable organizations={paginatedItems} networkId={networkId} />
          </CardBody>
        </Card>
      )}

      {totalCount > 0 ? (
        <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      ) : null}
    </VStack>
  );
};

export default NetworkReportOrganizationsTab;
